import { useId, useState } from 'react';

import type { Campaign } from '../api.ts';
import { ConfirmDialog } from '../ConfirmDialog.tsx';
import * as wording from '../wording.ts';
import { MAX_BLOCKED_REASON_LENGTH } from './CardFields.tsx';
import type { BoardCard } from './model.ts';
import { applies, BULK_ACTIONS, type BulkAction, type BulkInput } from './selection.ts';

/** What the server takes as a rejection reason (the content page's Reject dialog allows as much). */
const MAX_REJECTION_REASON_LENGTH = 1000;

/**
 * The bar at the foot of the board while cards are selected (US-028): how
 * many, and every action of Design reference § Bulk actions, each enabled
 * only when a selected card qualifies. Set campaign, Set publish by, Block…
 * and Reject… ask first, in a dialog of their own; the page runs the action.
 */
export function BulkBar({
  cards,
  campaigns,
  running,
  onRun,
  onClear,
}: {
  /** The selected cards, in board order. */
  readonly cards: readonly BoardCard[];
  readonly campaigns: readonly Campaign[];
  /** The action running now; the bar waits for it. */
  readonly running: BulkAction | null;
  readonly onRun: (action: BulkAction, input: BulkInput) => void;
  readonly onClear: () => void;
}) {
  const [asking, setAsking] = useState<BulkAction | null>(null);

  return (
    <div className="board-bulk" role="region" aria-label={wording.BULK_BAR}>
      <span className="board-bulk__count" aria-live="polite">
        {wording.bulkSelected(cards.length)}
      </span>
      <div className="board-bulk__actions">
        {BULK_ACTIONS.map((action) => {
          const enabled = applies(action, cards);
          return (
            <button
              key={action.id}
              type="button"
              className="button button--small"
              disabled={!enabled || running !== null}
              title={enabled ? undefined : wording.BULK_NONE_QUALIFIES}
              onClick={() => {
                if (action.asks) setAsking(action);
                else onRun(action, {});
              }}
            >
              {running?.id === action.id ? wording.BULK_WORKING : action.label}
            </button>
          );
        })}
        <button type="button" className="button button--small button--quiet" onClick={onClear}>
          {wording.BULK_CLEAR}
        </button>
      </div>
      {asking !== null && (
        <BulkDialog
          action={asking}
          count={cards.length}
          campaigns={campaigns}
          onCancel={() => {
            setAsking(null);
          }}
          onApply={(input) => {
            setAsking(null);
            onRun(asking, input);
          }}
        />
      )}
    </div>
  );
}

/** The one question an action asks before it runs: a campaign, a date or a reason (optional for Reject…). */
function BulkDialog({
  action,
  count,
  campaigns,
  onCancel,
  onApply,
}: {
  readonly action: BulkAction;
  readonly count: number;
  readonly campaigns: readonly Campaign[];
  readonly onCancel: () => void;
  readonly onApply: (input: BulkInput) => void;
}) {
  const fieldId = useId();
  const [campaignId, setCampaignId] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [reason, setReason] = useState('');

  const title =
    action.id === 'campaign'
      ? wording.BULK_CAMPAIGN_TITLE
      : action.id === 'due'
        ? wording.BULK_DUE_TITLE
        : action.id === 'reject'
          ? wording.BULK_REJECT_TITLE
          : wording.BULK_BLOCK_TITLE;
  const blank = action.id === 'block' && reason.trim() === '';
  const apply = (): void => {
    if (blank) return;
    if (action.id === 'campaign') onApply({ campaignId: campaignId === '' ? null : campaignId });
    else if (action.id === 'due') onApply({ dueOn: dueOn === '' ? null : dueOn });
    else if (action.id === 'reject') onApply({ rejectionReason: reason.trim() });
    else onApply({ blockedReason: reason.trim() });
  };

  return (
    <ConfirmDialog
      open
      title={title}
      confirmLabel={action.id === 'reject' ? wording.CONTENT_REJECT : wording.BULK_APPLY}
      danger={action.id === 'reject'}
      confirmDisabled={blank}
      onConfirm={apply}
      onCancel={onCancel}
    >
      <p>{wording.bulkDialogBody(count)}</p>
      {action.id === 'reject' && <p>{wording.REJECT_BODY}</p>}
      <div className="field">
        {action.id === 'campaign' && (
          <>
            <label className="field__label" htmlFor={fieldId}>
              {wording.BULK_CAMPAIGN_FIELD}
            </label>
            <select
              id={fieldId}
              className="field__input"
              value={campaignId}
              onChange={(event) => {
                setCampaignId(event.target.value);
              }}
            >
              <option value="">{wording.BOARD_NO_CAMPAIGN}</option>
              {campaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </select>
          </>
        )}
        {action.id === 'due' && (
          <>
            <label className="field__label" htmlFor={fieldId}>
              {wording.BULK_DUE_FIELD}
            </label>
            <input
              id={fieldId}
              className="field__input"
              type="date"
              value={dueOn}
              onChange={(event) => {
                setDueOn(event.target.value);
              }}
            />
            <p className="field__hint">{wording.BULK_DUE_HINT}</p>
          </>
        )}
        {action.id === 'block' && (
          <>
            <label className="field__label" htmlFor={fieldId}>
              {wording.BULK_BLOCK_FIELD}
            </label>
            <input
              id={fieldId}
              className="field__input"
              type="text"
              maxLength={MAX_BLOCKED_REASON_LENGTH}
              // The reason is the whole dialog: typing starts here, not on Cancel.
              autoFocus
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                apply();
              }}
            />
            <p className="field__hint">{wording.BULK_BLOCK_HINT}</p>
          </>
        )}
        {action.id === 'reject' && (
          <>
            <label className="field__label" htmlFor={fieldId}>
              {wording.REJECT_REASON_LABEL}
            </label>
            <textarea
              id={fieldId}
              className="field__textarea"
              rows={3}
              maxLength={MAX_REJECTION_REASON_LENGTH}
              autoFocus
              placeholder={wording.REJECT_REASON_PLACEHOLDER}
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
            <p className="field__hint">{wording.BULK_REJECT_HINT}</p>
          </>
        )}
      </div>
    </ConfirmDialog>
  );
}
